import React, { useState, useEffect, useCallback } from 'react';
// Removed Redux auth selectors - now using useAuth() hook
import { useRole } from '../../hooks/useRole';
import { UserRole } from '../../types/enums';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AuditService } from '../../services/auditService';
import { getUsersForManagement } from '../../services/userService';

// Styled Components - Purple/Blue Gradient Header
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(1.5),
  borderRadius: '8px',
  boxShadow: '0 4px 16px rgba(102, 126, 234, 0.2)',
}));

const HeaderButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
}));

const AuditReports = () => {
  const { hasRole } = useRole();
  
  // All hooks must be called before any early returns
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  
  // Data states
  const [clockEvents, setClockEvents] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});

  const fetchFacilities = async () => {
    try {
      const facilitiesData = await AuditService.getFacilities();
      // Ensure facilitiesData is always an array
      setFacilities(Array.isArray(facilitiesData) ? facilitiesData : []);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      // Set empty array on error to prevent .map() errors
      setFacilities([]);
    }
  };

  const fetchUsers = async () => {
    try {
      // Use cache service for secure user data access
      const usersData = await getUsersForManagement();
      // getUsersForManagement returns { users: [...] }, so extract the users array
      const usersArray = usersData?.users || [];
      setUsers(Array.isArray(usersArray) ? usersArray : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Set empty array on error to prevent .map() errors
      setUsers([]);
    }
  };

  const fetchClockEvents = useCallback(async () => {
    try {
      const events = await AuditService.getClockEvents(startDate, endDate, 500);
      // Ensure events is always an array
      const eventsArray = Array.isArray(events) ? events : [];
      setClockEvents(eventsArray);
      return eventsArray;
    } catch (error) {
      console.error('Error fetching clock events:', error);
      // Set empty array on error to prevent .map() errors
      setClockEvents([]);
      return [];
    }
  }, [startDate, endDate]);

  const fetchAuditLogs = useCallback(async () => {
    try {
      const attendanceLogs = await AuditService.getAuditLogs(startDate, endDate, 500);
      // Ensure attendanceLogs is always an array
      const logsArray = Array.isArray(attendanceLogs) ? attendanceLogs : [];
      setAuditLogs(logsArray);
      return logsArray;
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      // Set empty array on error to prevent .map() errors
      setAuditLogs([]);
      return [];
    }
  }, [startDate, endDate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchClockEvents(),
        fetchAuditLogs()
      ]);
    } catch (error) {
      console.error('Error fetching audit data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchClockEvents, fetchAuditLogs]);

  // Handle date changes and refresh data
  const handleDateChange = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchClockEvents(),
        fetchAuditLogs()
      ]);
    } catch (error) {
      console.error('Error refreshing data after date change:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchClockEvents, fetchAuditLogs]);

  // useEffect hooks must also be called before any conditional returns
  useEffect(() => {
    fetchFacilities();
    fetchUsers();
    // Call fetchData directly without dependency to prevent infinite loops
    const initialFetch = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchClockEvents(),
          fetchAuditLogs()
        ]);
      } catch (error) {
        console.error('Error fetching audit data:', error);
      } finally {
        setLoading(false);
      }
    };
    initialFetch();
  }, [fetchClockEvents, fetchAuditLogs]); // Include dependencies since they're stable due to useCallback

  // Update summary when data changes (but only when there's actual data)
  useEffect(() => {
    if (clockEvents.length > 0 || auditLogs.length > 0) {
      const clockSummary = Array.isArray(clockEvents) ? AuditService.generateClockEventsSummary(clockEvents) : {};
      setSummary({
        ...clockSummary,
        totalAuditLogs: Array.isArray(auditLogs) ? auditLogs.length : 0
      });
    }
  }, [clockEvents, auditLogs]);

  // Remove the problematic useEffect that was causing infinite loops
  // useEffect(() => {
  //   if (Array.isArray(clockEvents) && clockEvents.length > 0) {
  //     generateSummary();
  //   }
  // }, [clockEvents, generateSummary]);

  // Check if user has access to audit reports (after all hooks)
  const hasAccess = hasRole(UserRole.ADMIN) || hasRole(UserRole.FACILITY_ADMIN);

  if (!hasAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography>
            You do not have permission to view audit reports. This feature is restricted to administrators and facility administrators only.
          </Typography>
        </Alert>
      </Box>
    );
  }



  const getUserName = (userId) => {
    if (!Array.isArray(users)) return userId;
    const user = users.find(u => u.uid === userId || u.id === userId);
    return user ? (user.name || user.email) : userId;
  };

  const getFacilityName = (facilityId) => {
    if (!Array.isArray(facilities)) return facilityId;
    const facility = facilities.find(f => f.id === facilityId);
    return facility ? facility.name : facilityId;
  };

  const getEventStatusColor = (event) => {
    if (event.managerOverride === true) return 'warning';
    if (typeof event.action === 'string' && event.action === 'clock_in') return 'success';
    if (typeof event.action === 'string' && event.action === 'clock_out') return 'info';
    return 'default';
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'User', 'Facility', 'Action', 'Overtime Hours', 'Manager Override'];
    const csvData = [
      headers,
      ...(Array.isArray(clockEvents) ? clockEvents.map(event => [
        (event.timestamp instanceof Date ? event.timestamp.toLocaleDateString() : ''),
        (event.timestamp instanceof Date ? event.timestamp.toLocaleTimeString() : ''),
        getUserName(event.userId),
        getFacilityName(event.facilityId),
        (typeof event.action === 'string' ? event.action.replace('_', ' ') : event.action || '').toUpperCase(),
        (typeof event.overtimeHours === 'number' ? event.overtimeHours : 0),
        (event.managerOverride === true ? 'Yes' : 'No')
      ]) : [])
    ];

    const csvContent = Array.isArray(csvData) ? csvData.map(row => Array.isArray(row) ? row.join(',') : '').join('\n') : '';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clock_events_${startDate instanceof Date ? startDate.toISOString().split('T')[0] : 'unknown'}_${endDate instanceof Date ? endDate.toISOString().split('T')[0] : 'unknown'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card with Purple/Blue Gradient */}
      <HeaderCard>
        <CardContent sx={{ py: 1.5, px: 3, '&:last-child': { pb: 1.5 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <AssessmentIcon sx={{ fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Calendar Audit Reports
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>
                  Monitor attendance, clock events, and system activity
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Export to CSV">
                <HeaderButton onClick={exportToCSV}>
                  <DownloadIcon />
                </HeaderButton>
              </Tooltip>
              <Tooltip title="Refresh Data">
                <HeaderButton onClick={fetchData}>
                  <RefreshIcon />
                </HeaderButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.totalClockIns || 0}</Typography>
                  <Typography variant="caption">Clock Ins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TimeIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.totalClockOuts || 0}</Typography>
                  <Typography variant="caption">Clock Outs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ErrorIcon color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.overdueClockOuts || 0}</Typography>
                  <Typography variant="caption">Overdue Clock Outs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.managerOverrides || 0}</Typography>
                  <Typography variant="caption">Manager Overrides</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EventIcon color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.overtimeEvents || 0}</Typography>
                  <Typography variant="caption">Overtime Events</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{summary.totalAuditLogs || 0}</Typography>
                  <Typography variant="caption">Audit Logs</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newDate) => {
                  setStartDate(newDate);
                  // Refresh data when start date changes
                  setTimeout(handleDateChange, 100);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newDate) => {
                  setEndDate(newDate);
                  // Refresh data when end date changes
                  setTimeout(handleDateChange, 100);
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                label="Facility"
              >
                <MenuItem value="all">All Facilities</MenuItem>
                {Array.isArray(facilities) && facilities.map((facility) => (
                  <MenuItem key={facility.id || `facility-${Math.random()}`} value={facility.id || ''}>
                    {facility.name || 'Unknown Facility'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                label="User"
              >
                <MenuItem value="all">All Users</MenuItem>
                {Array.isArray(users) && users.map((user) => (
                  <MenuItem key={user.id || user.uid || `user-${Math.random()}`} value={user.uid || user.id || ''}>
                    {user.name || user.email || 'Unknown User'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              onClick={handleDateChange}
              disabled={loading}
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            >
              {loading ? 'Loading...' : 'Apply Filters'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`Clock Events (${Array.isArray(clockEvents) ? clockEvents.length : 0})`} />
          <Tab label={`Audit Logs (${Array.isArray(auditLogs) ? auditLogs.length : 0})`} />
        </Tabs>
        <Divider />
        
        <Box sx={{ p: 2 }}>
          {activeTab === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Facility</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Shift Times</TableCell>
                    <TableCell>Overtime</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(clockEvents) && clockEvents.map((event) => (
                    <TableRow key={event.id || `event-${Math.random()}`}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {event.timestamp instanceof Date ? event.timestamp.toLocaleDateString() : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.timestamp instanceof Date ? event.timestamp.toLocaleTimeString() : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                          {getUserName(event.userId)}
                        </Box>
                      </TableCell>
                      <TableCell>{getFacilityName(event.facilityId)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={(typeof event.action === 'string' && event.action === 'clock_in') ? <CheckIcon /> : <TimeIcon />}
                          label={(typeof event.action === 'string' ? event.action.replace('_', ' ') : event.action || '').toUpperCase()}
                          color={getEventStatusColor(event)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          {event.confirmedShiftStart && (
                            <Typography variant="caption" display="block">
                              Start: {(() => {
                                try {
                                  const date = new Date(event.confirmedShiftStart);
                                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleTimeString();
                                } catch {
                                  return 'Invalid Date';
                                }
                              })()}
                            </Typography>
                          )}
                          {event.confirmedShiftEnd && (
                            <Typography variant="caption" display="block">
                              End: {(() => {
                                try {
                                  const date = new Date(event.confirmedShiftEnd);
                                  return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleTimeString();
                                } catch {
                                  return 'Invalid Date';
                                }
                              })()}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(typeof event.overtimeHours === 'number' && event.overtimeHours > 0) && (
                          <Box>
                            <Chip 
                              label={`${typeof event.overtimeHours === 'number' ? event.overtimeHours : 0}h`}
                              color="warning"
                              size="small"
                            />
                            {(typeof event.overtimeReason === 'string' && event.overtimeReason.trim() !== '') && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                {event.overtimeReason}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          {(event.managerOverride === true) && (
                            <Chip 
                              icon={<WarningIcon />}
                              label="Manager Override"
                              color="warning"
                              size="small"
                            />
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {activeTab === 1 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Target Type</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(auditLogs) && auditLogs.map((log) => (
                    <TableRow key={log.id || `log-${Math.random()}`}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {log.timestamp instanceof Date ? log.timestamp.toLocaleDateString() : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.timestamp instanceof Date ? log.timestamp.toLocaleTimeString() : ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                          {getUserName(log.userId)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.action}
                          color={typeof log.action === 'string' && log.action.includes('ERROR') ? 'error' : 'primary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.targetType}</TableCell>
                      <TableCell>
                        <Box sx={{ maxWidth: 300 }}>
                          {(log.details !== null && log.details !== undefined) && (
                            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                              {typeof log.details === 'object' && log.details !== null
                                ? JSON.stringify(log.details, null, 2)
                                : (log.details || '')
                              }
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" mt={3}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default AuditReports; 